import { DDSCheckbox, DDSTypography } from 'den-design-system'
import React, { useEffect, useState } from 'react'

import Input from '../../New/Input/Input'
import RegexConstants from '../../../constants/RegexConstants'
import { SettingAddPopup } from '../../../pages/NewVersion/Settings/SettingPopups/SettingAddPopup'
import ToastNotification from '../DDS/Toast/Toast'
import UserAvatar from '../UserAvatar/UserAvatar'
import WarningIcon from '../../../assets/icons/warningIcon.svg'
import { observer } from 'mobx-react-lite'
import pallete from '../../../global/newPallete'
import { updateUser } from '../../../api/user/User.service'
import { useStoreContext } from '../../../store/StoreContext'

interface MissingPhoneAlertGroupProps {
  users: {
    id: string
    name: string
    imgUrl: string
    phoneNumber?: string
    userId?: string
  }[]
  onPhoneUpdated?: () => void
  id: any
  avatarObjects?: {
    name: string
    avatar: string
    firstName: string
    lastName: string
  }[]

  projectMemberIds?: any
  onSelectedUserUpdate: (updatedUser: any) => void
}

const MissingPhoneAlertGroup: React.FC<MissingPhoneAlertGroupProps> = ({
  users,
  onPhoneUpdated,
  id,
  avatarObjects,
  projectMemberIds,
  onSelectedUserUpdate
}) => {
  const store = useStoreContext()
  const currentUserId = store.userStore.getId()

  const [addPhoneNumber, setAddPhoneNumber] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [checked, setChecked] = useState(false)
  const [phoneFocus, setPhoneFocus] = useState(false)
  const [phoneInvalid, setPhoneInvalid] = useState(false)

  const [currentPhone, setCurrentPhone] = useState(
    store.userStore.getPhoneNumber()
  )

  useEffect(() => {
    const phone = store.userStore.getPhoneNumber()
    const check = store.userStore.getIsSmsOptedIn()
    setCurrentPhone(phone)
    setChecked(check)
  }, [store.userStore.getPhoneNumber(), store.userStore.getIsSmsOptedIn()])

  const isCurrentUserSelected =
    users?.some((user) => user.userId === currentUserId) && !currentPhone

  const isOtherSelectedUserWithoutPhone = users?.some(
    (user) =>
      user.userId !== currentUserId &&
      (!user.phoneNumber || user.phoneNumber.trim() === '')
  )

  const updatePhoneNumber = (phoneNumber: string, checked: boolean) => {
    const trimmed = phoneNumber.trim()
    const prevPhone = store.userStore.getPhoneNumber()
    const prevChecked = store.userStore.getIsSmsOptedIn()

    const phoneChanged = trimmed !== prevPhone
    const checkedChanged = checked !== prevChecked

    setLoading(true)
    updateUser({ phoneNumber: trimmed, isSmsOptedIn: checked })
      .then((response: any) => {
        store.userStore.setPhoneNumber(response.data.phoneNumber)
        store.userStore.setIsSmsOptedIn(response.data.isSmsOptedIn)
        setPhoneNumber(response.data.phoneNumber)
        setChecked(response.data.isSmsOptedIn)

        if (phoneChanged && !checkedChanged) {
          if (!checked) {
            ToastNotification({
              type: 'success',
              message: 'Phone number updated successfully'
            })
            ToastNotification({
              type: 'info',
              message:
                'You have not opted in for SMS alerts. Without checking this box, you will not receive escalation notifications via SMS'
            })
          } else {
            ToastNotification({
              type: 'success',
              message: 'Phone number updated successfully'
            })
          }
        } else if (phoneChanged && checkedChanged && checked) {
          ToastNotification({
            type: 'success',
            message: 'Phone number and SMS alerts enabled successfully'
          })
        }
        setAddPhoneNumber(false)
        setLoading(false)
        setPhoneFocus(false)
        onPhoneUpdated?.()
      })
      .catch((err: string) => {
        setLoading(false)
        setPhoneNumber(store.userStore.getPhoneNumber())
        setChecked(store.userStore.getIsSmsOptedIn())
        setPhoneFocus(false)

        ToastNotification({ type: 'error', message: err })
      })
  }

  if (users.length === 0) return null

  return (
    <>
      {isCurrentUserSelected ? (
        <div
          className='flex items-center rounded-[6px] p-[12px] space-x-[12px] w-full'
          style={{ backgroundColor: pallete.colors.warningRed5 }}
        >
          <div className='text-white rounded-full w-[24px] h-[24px] flex items-center justify-center'>
            <img
              src={WarningIcon}
              alt='Warning'
              className='w-[20px] h-[20px] rounded-full border-[2px] border-white shadow-md'
            />
          </div>

          {/* Message */}
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark3}
          >
            You haven’t added a phone number. Add one in Account Settings to
            enable SMS escalations.
            {isCurrentUserSelected && (
              <>
                {' '}
                <span
                  className='underline cursor-pointer'
                  style={{ color: pallete.colors.primary2 }}
                  onClick={() => setAddPhoneNumber(true)}
                >
                  Add Phone Number
                </span>
              </>
            )}
          </DDSTypography.Paragraph>
        </div>
      ) : isOtherSelectedUserWithoutPhone ? (
        <div
          className='flex items-center rounded-[6px] p-[12px] space-x-[12px] w-full'
          style={{ backgroundColor: pallete.colors.warningRed5 }}
        >
          <div className='text-white rounded-full w-[24px] h-[24px] flex items-center justify-center'>
            <img
              src={WarningIcon}
              alt='Warning'
              className='w-[20px] h-[20px] rounded-full border-[2px] border-white shadow-md'
            />
          </div>

          {/* Render all avatars */}
          <div className='flex space-x-[4px]'>
            {/* Render up to 3 avatars, then show +N if more */}
            <div className='flex space-x-[4px] items-center'>
              <UserAvatar
                id={`menu-list-btn${id && '-' + id}`}
                avatarType='Multiple'
                imageObjectGroup={avatarObjects}
              />
            </div>
          </div>

          {/* Message */}
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark3}
          >
            need to add their phone number in Account Settings to enable SMS
            escalations.
          </DDSTypography.Paragraph>
        </div>
      ) : null}

      <div className='override-model-verify-min-width'>
        <SettingAddPopup
          width={'56%'}
          showHeader
          title='Add Phone Number'
          modalOpen={addPhoneNumber}
          setModalOpen={setAddPhoneNumber}
          primaryButtonText='Save'
          primaryButtonClick={() => {
            const value = phoneNumber.trim()
            const current = store.userStore.getPhoneNumber()

            if (
              value !== current &&
              (value === '' || RegexConstants.E164_REGEX.test(value))
            ) {
              updatePhoneNumber(value, checked)
            } else {
              setPhoneFocus(false)
            }
          }}
          primaryButtonDisabled={loading || phoneNumber.trim() === ''}
          loading={loading}
          secondaryButtonText='Cancel'
          secondaryButtonClick={() => {
            setPhoneNumber('')
            setAddPhoneNumber(false)
          }}
          secondaryButtonDisabled={loading}
          showFooter={true}
          content={
            <div className='flex flex-col p-[24px] gap-[16px]'>
              <div>
                <DDSTypography.Title
                  type='h5'
                  variant='medium'
                  color={pallete.colors.textDark3}
                >
                  Please enter your phone number
                </DDSTypography.Title>
              </div>
              <Input
                type='text'
                labelPosition='top'
                id='phone-number-input'
                name='phoneNumber'
                value={phoneNumber}
                onChange={(e: any) => {
                  setPhoneNumber(e.target.value)
                }}
                onFocus={() => {
                  setPhoneFocus(true)
                }}
                validateInput={true}
                validate={(value: string) => {
                  value = value.trim()
                  if (value === '') {
                    return { error: false, message: '' }
                  }
                  if (!RegexConstants.E164_REGEX.test(value)) {
                    return {
                      error: true,
                      message: 'Enter a valid phone number'
                    }
                  }
                  return { error: false, message: '' }
                }}
                onError={(error) => {
                  setPhoneInvalid(error)
                }}
                placeholder='+911234512345'
              />

              <div className='override-checkbox-phone-number'>
                <DDSCheckbox
                  label={
                    <DDSTypography.Paragraph
                      size='caption'
                      variant='medium'
                      color={pallete.colors.neuButtonText}
                    >
                      By opting in, you agree to receive Vigil Alerts via SMS.
                      Message frequency varies based on alert activity. Message
                      and data rates may apply. Your mobile information will
                      not be sold or shared with third parties for promotional
                      or marketing purposes. You can opt out at any time by
                      unchecking this option or contacting{' '}
                      <a
                        href='mailto:support@vigilnow.com'
                        style={{
                          color: pallete.colors.primary2,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        support@vigilnow.com
                      </a>
                      . View our{' '}
                      <a
                        href='https://www.vigilnow.com/terms-and-conditions'
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{
                          color: pallete.colors.primary2,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        Terms & Conditions
                      </a>{' '}
                      and{' '}
                      <a
                        href='https://www.vigilnow.com/privacy-policy'
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{
                          color: pallete.colors.primary2,
                          textDecoration: 'underline',
                          cursor: 'pointer'
                        }}
                      >
                        Privacy Policy
                      </a>
                      .
                    </DDSTypography.Paragraph>
                  }
                  checked={checked}
                  onChange={() => {
                    setChecked(!checked)
                  }}
                  disabled={store.userStore.getNoActiveSubscription()}
                />
              </div>
            </div>
          }
        />
      </div>
    </>
  )
}

export default observer(MissingPhoneAlertGroup)
